// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { color, mergeWithCss, xScreen } from "@adl/XDL/Utilities";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { Children, FC, useMemo, useState } from "react";
import { css } from "styled-components";

interface ProductGridLayoutContextType {
    styles: ProductGridLayoutStyle;
}
export interface ProductGridLayoutStyle {
    container?: InjectableCss;
    info?: InjectableCss;
    priceAndActions?: InjectableCss;
    priceSection?: InjectableCss;
    availabilitySection?: InjectableCss;
    addToListSection?: InjectableCss;
    addToCartSection?: InjectableCss;
    image?: InjectableCss;
}

const productGridLayoutStyle: ProductGridLayoutStyle = {
    container: {
        css: css`
            position: relative;
            display: flex;
            flex-direction: column;
            max-width: 308px;
            width: 100%;
            height: 100%;
            ${xScreen("lte", "sm")} {
                &&& {
                    max-width: 308px;
                    margin: 0 auto;
                }
            }
            ${xScreen("lte", "md")} {
                max-width: 230px;
            }
        `,
    },
    image: {
        css: css`
            position: relative;
            background-color: ${color("common.white")};
            display: flex;
            justify-content: center;
            align-items: center;
            width: 100%;
            height: 287px;
            max-width: 287px;
            max-height: 287px;
            margin-bottom: 12px;
            padding: 0 10px;
            img {
                width: auto;
                height: auto;
                max-width: 100%;
                max-height: 267px;
            }
            ${xScreen("lte", "sm")} {
                padding: 0;
            }
        `,
    },
    info: {
        css: css`
            display: flex;
            flex-direction: column;
            margin-bottom: 12px;
            padding: 0 10px;
            ${xScreen("lte", "sm")} {
                padding: 0;
            }
        `,
    },
    priceSection: {
        css: css`
            display: flex;
            justify-content: space-between;
            margin-top: auto;
            min-height: 37px;
        `,
    },
    availabilitySection: {
        css: css`
            display: flex;
            flex-direction: column;
            margin-bottom: 0;
            margin-bottom: 12px;
        `,
    },
    addToCartSection: {
        css: css`
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 12px;
        `,
    },
    addToListSection: {
        css: css`
            align-items: center;
            display: flex;
            justify-content: space-between;
        `,
    },
    priceAndActions: {
        css: css`
            margin-top: auto;
            display: flex;
            flex-direction: column;
            padding: 0 10px;
            ${xScreen("lte", "sm")} {
                padding: 0;
            }
        `,
    },
};

const ProductGridLayoutContext = React.createContext<ProductGridLayoutContextType>({
    styles: {},
});

export const useProductGridLayoutContext = () => {
    const context = React.useContext(ProductGridLayoutContext);
    if (!context) {
        throw new Error("ProductGridLayout compound components cannot be rendered outside ProductGridLayout");
    }
    return context;
};

const ProductGridLayout = ({
    children,
    extendedStyles,
    ...otherProps
}: {
    children: Array<React.ReactElement>;
    extendedStyles?: ProductGridLayoutStyle;
    [key: string]: any;
}) => {
    const [styles] = useState(() => mergeWithCss(productGridLayoutStyle, extendedStyles));
    const value = useMemo(() => ({ styles }), [styles]);
    return (
        <ProductGridLayoutContext.Provider value={value}>
            <StyledWrapper {...styles.container} {...otherProps} data-test-selector="product-grid-layout-container">
                {children}
            </StyledWrapper>
        </ProductGridLayoutContext.Provider>
    );
};

const Image: FC = ({ children, ...otherProps }) => {
    const {
        styles: { image },
    } = useProductGridLayoutContext();

    return (
        <StyledWrapper {...otherProps} {...image} data-test-selector="product-grid-layout-image">
            {children}
        </StyledWrapper>
    );
};

const Info: FC = ({ children, ...otherProps }) => {
    const {
        styles: { info },
    } = useProductGridLayoutContext();

    return (
        <StyledWrapper {...otherProps} {...info} data-test-selector="product-grid-layout-info">
            {children}
        </StyledWrapper>
    );
};

const PriceAndActionsSection: FC = ({ children, ...otherProps }) => {
    const {
        styles: { priceAndActions },
    } = useProductGridLayoutContext();

    return (
        <StyledWrapper {...otherProps} {...priceAndActions} data-test-selector="product-grid-layout-price-and-actions">
            {children}
        </StyledWrapper>
    );
};
const PriceSection: FC = ({ children, ...otherProps }) => {
    const {
        styles: { priceSection },
    } = useProductGridLayoutContext();

    return (
        <StyledWrapper {...otherProps} {...priceSection} data-test-selector="product-grid-layout-price">
            {children}
        </StyledWrapper>
    );
};

const AvailabilitySection: FC = ({ children, ...otherProps }) => {
    const {
        styles: { availabilitySection },
    } = useProductGridLayoutContext();

    return (
        <StyledWrapper {...otherProps} {...availabilitySection} data-test-selector="product-grid-layout-availability">
            {children}
        </StyledWrapper>
    );
};

const AddToCartSection: FC = ({ children, ...otherProps }) => {
    const {
        styles: { addToCartSection },
    } = useProductGridLayoutContext();

    return (
        <StyledWrapper {...otherProps} {...addToCartSection} data-test-selector="product-grid-layout-cart-actions">
            {children}
        </StyledWrapper>
    );
};

const AddToListAction: FC = ({ children, ...otherProps }) => {
    const {
        styles: { addToListSection },
    } = useProductGridLayoutContext();

    return (
        <StyledWrapper {...otherProps} {...addToListSection} data-test-selector="product-grid-layout-add-to-list">
            {children}
        </StyledWrapper>
    );
};

ProductGridLayout.Image = Image;
ProductGridLayout.Info = Info;
ProductGridLayout.PriceAndActionsSection = PriceAndActionsSection;
ProductGridLayout.PriceSection = PriceSection;
ProductGridLayout.AvailabilitySection = AvailabilitySection;
ProductGridLayout.AddToCartSection = AddToCartSection;
ProductGridLayout.AddToListAction = AddToListAction;

export default ProductGridLayout;
